import React from "react";
import {
  clientWorkspaceBreadcrumbs,
  useClientWorkspace,
} from "../components/ClientWorkspace";
import {
  ClientPrimaryNavigationKey,
  ClientSecondaryNavigationKey,
  GlobalPortalNavigationKey,
  insightsNavigation,
  treatmentNavigation,
} from "../config/navigation";
import { EmptyState } from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import {
  ClientPrimaryTabs,
  SegmentedNavigation,
} from "../components/PortalNavigation";

const primaryLabels: Record<ClientPrimaryNavigationKey, string> = {
  profile: "Client Profile",
  "current-homework": "Current Homework",
  "homework-history": "Homework History",
  sessions: "Sessions",
  treatment: "Treatment",
  insights: "Insights",
};

interface DeferredPortalPageProps {
  clinicianCode: string;
  activeGlobalKey?: GlobalPortalNavigationKey;
  title: string;
}

export function DeferredPortalPage({
  clinicianCode,
  activeGlobalKey,
  title,
}: DeferredPortalPageProps) {
  return (
    <ClinicianPortalLayout
      activeGlobalKey={activeGlobalKey}
      clinicianCode={clinicianCode}
    >
      <PortalHeader
        title={title}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[
              { label: "Portal", path: "/clinician-portal" },
              { label: title },
            ]}
          />
        }
      />
      <div className="cp-page-content">
        <EmptyState
          title="Not available in this vertical slice"
          description="This page is intentionally deferred until its approved Portal DATA contract is ready for the React migration."
        />
      </div>
    </ClinicianPortalLayout>
  );
}

export function DeferredClientPage({
  clinicianCode,
  patientId,
  primaryKey,
  secondaryKey,
}: {
  clinicianCode: string;
  patientId: string;
  primaryKey: Exclude<ClientPrimaryNavigationKey, "profile">;
  secondaryKey?: ClientSecondaryNavigationKey;
}) {
  const workspace = useClientWorkspace();
  const secondaryItems =
    primaryKey === "treatment"
      ? treatmentNavigation
      : primaryKey === "insights"
        ? insightsNavigation
        : undefined;

  return (
    <ClinicianPortalLayout
      activeGlobalKey={workspace.source === "team" ? "team" : "clients"}
      clinicianCode={clinicianCode}
    >
      <PortalHeader
        title={primaryLabels[primaryKey]}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={clientWorkspaceBreadcrumbs(
              workspace,
              patientId,
              workspace.teamContext?.client.nickname || primaryLabels[primaryKey],
              secondaryKey
                ? secondaryItems?.find((item) => item.key === secondaryKey)?.label
                : undefined,
            )}
          />
        }
      >
        <ClientPrimaryTabs
          patientId={patientId}
          clinicianCode={clinicianCode}
          activeKey={primaryKey}
        />
      </PortalHeader>
      <div className="cp-page-content cp-page-content--profile">
        {secondaryItems ? (
          <SegmentedNavigation
            items={secondaryItems}
            patientId={patientId}
            clinicianCode={clinicianCode}
            activeKey={secondaryKey}
          />
        ) : null}
        <EmptyState
          title="Not available in this vertical slice"
          description="This section is intentionally deferred. No substitute data source or frontend business logic has been added."
        />
      </div>
    </ClinicianPortalLayout>
  );
}

export function DeferredHomeworkDetailPage({
  clinicianCode,
  patientId,
  primaryKey,
}: {
  clinicianCode: string;
  patientId: string;
  primaryKey: "current-homework" | "homework-history";
}) {
  const sourceLabel =
    primaryKey === "homework-history"
      ? "Homework History"
      : "Current Homework";
  return (
    <ClinicianPortalLayout
      activeGlobalKey="clients"
      clinicianCode={clinicianCode}
    >
      <PortalHeader
        title="Homework Detail"
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[
              { label: "My Clients", path: "/clinician-portal/clients" },
              {
                label: sourceLabel,
                path: `/clinician-portal/clients/${encodeURIComponent(
                  patientId,
                )}/${primaryKey}`,
              },
              { label: "Homework Detail" },
            ]}
          />
        }
      >
        <ClientPrimaryTabs
          patientId={patientId}
          clinicianCode={clinicianCode}
          activeKey={primaryKey}
        />
      </PortalHeader>
      <div className="cp-page-content cp-page-content--profile">
        <EmptyState
          title="Homework Detail is not yet migrated"
          description="The route is ready for the next approved slice. No detail data has been requested or fabricated here."
        />
      </div>
    </ClinicianPortalLayout>
  );
}
